import type {
    ConvertQuotationToInvoiceBody,
    CreateDocumentDTO,
    CreateDocumentPaymentInput,
    DocumentDetailResult,
    DocumentsListResult,
    DocumentsSummaryResult,
    HistoryListResult,
    HistorySummaryResult,
    QuotationInvoiceCustomerCategoryListItem,
    QuotationInvoiceCustomerListItem,
    QuotationInvoiceDocumentTypeListItem,
    QuotationInvoiceIssuerSettingsDTO,
    UpdateDocumentPaymentInput,
    UpdateIssuerSettingsInput,
} from "@/modules/quotation-invoice/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";

const baseUrl = `${config.apiUrl}/quotation-invoice`;

/** ອ່ານ `message` ຈາກ JSON body ກ່ອນ ແລ້ວຈຶ່ງໃຊ້ statusText ເປັນຮອງ */
async function throwQuotationInvoiceHttpError(
  res: Response,
  fallbackPrefix: string,
): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    error?: string;
    shortfalls?: import("./catalog-types").QiStockShortfallDTO[];
  };
  const msg = body.message?.trim();
  if (body.error === "INSUFFICIENT_STOCK" && body.shortfalls?.length) {
    const err = new Error(msg || "ສຕັອກບໍ່ພໍ");
    Object.assign(err, { code: "INSUFFICIENT_STOCK", shortfalls: body.shortfalls });
    throw err;
  }
  if (msg) throw new Error(msg);
  if (typeof body.error === "string" && body.error.trim()) {
    throw new Error(body.error.trim());
  }
  throw new Error(`${fallbackPrefix}: ${res.statusText}`);
}

export type CustomersLookupResult = {
  items: QuotationInvoiceCustomerListItem[];
  total: number;
};

export type CreateCustomerInput = {
  name: string;
  address?: string | null;
  contact?: string | null;
  taxId?: string | null;
  categoryId?: string | null;
};

export type UpdateCustomerInput = {
  name?: string;
  address?: string | null;
  contact?: string | null;
  taxId?: string | null;
  categoryId?: string | null;
  isActive?: boolean;
};

export type CustomerCategoriesLookupResult = {
  items: QuotationInvoiceCustomerCategoryListItem[];
  total: number;
};

export type CreateCustomerCategoryInput = {
  name: string;
  sortOrder?: number;
};

export type UpdateCustomerCategoryInput = {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
};

export type DocumentTypesLookupResult = {
  items: QuotationInvoiceDocumentTypeListItem[];
  total: number;
};

export const quotationInvoiceApi = {
  async getCustomers(
    params: {
      q?: string;
      limit?: number;
      skip?: number;
      includeInactive?: boolean;
      categoryId?: string;
    } = {},
  ): Promise<CustomersLookupResult> {
    const search = new URLSearchParams();
    if (params.q != null) search.set("q", params.q);
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.skip != null) search.set("skip", String(params.skip));
    if (params.includeInactive === true) search.set("includeInactive", "true");
    if (params.categoryId != null && params.categoryId !== "")
      search.set("categoryId", params.categoryId);
    const res = await fetch(`${baseUrl}/customers?${search.toString()}`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch customers: ${res.statusText}`);
    return res.json();
  },

  async createCustomer(
    input: CreateCustomerInput,
  ): Promise<QuotationInvoiceCustomerListItem> {
    const res = await fetch(`${baseUrl}/customers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (!res.ok)
      throw new Error(`Failed to create customer: ${res.statusText}`);
    return res.json();
  },

  async updateCustomer(
    id: string,
    input: UpdateCustomerInput,
  ): Promise<QuotationInvoiceCustomerListItem> {
    const res = await fetch(`${baseUrl}/customers/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (!res.ok)
      throw new Error(`Failed to update customer: ${res.statusText}`);
    return res.json();
  },

  async getCustomer(
    id: string,
  ): Promise<QuotationInvoiceCustomerListItem | null> {
    const res = await fetch(`${baseUrl}/customers/${id}`, {
      credentials: "include",
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Failed to fetch customer: ${res.statusText}`);
    return res.json();
  },

  async getCustomerCategories(
    params: { includeInactive?: boolean } = {},
  ): Promise<CustomerCategoriesLookupResult> {
    const search = new URLSearchParams();
    if (params.includeInactive === true) search.set("includeInactive", "true");
    const res = await fetch(
      `${baseUrl}/customer-categories?${search.toString()}`,
      { credentials: "include" },
    );
    if (!res.ok)
      throw new Error(`Failed to fetch customer categories: ${res.statusText}`);
    return res.json();
  },

  async createCustomerCategory(
    input: CreateCustomerCategoryInput,
  ): Promise<QuotationInvoiceCustomerCategoryListItem> {
    const res = await fetch(`${baseUrl}/customer-categories`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (!res.ok)
      throw new Error(`Failed to create category: ${res.statusText}`);
    return res.json();
  },

  async updateCustomerCategory(
    id: string,
    input: UpdateCustomerCategoryInput,
  ): Promise<QuotationInvoiceCustomerCategoryListItem> {
    const res = await fetch(`${baseUrl}/customer-categories/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (!res.ok)
      throw new Error(`Failed to update category: ${res.statusText}`);
    return res.json();
  },

  async getDocumentTypes(
    params: { limit?: number; includeInactive?: boolean } = {},
  ): Promise<DocumentTypesLookupResult> {
    const search = new URLSearchParams();
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.includeInactive === true) search.set("includeInactive", "true");
    const res = await fetch(`${baseUrl}/document-types?${search.toString()}`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch document types: ${res.statusText}`);
    return res.json();
  },

  async getNextDocumentNumber(params: {
    documentType: string;
    year?: number;
    excludeDocumentId?: string;
  }): Promise<{ documentNumber: string }> {
    const search = new URLSearchParams();
    search.set("documentType", params.documentType);
    if (params.year != null) search.set("year", String(params.year));
    if (params.excludeDocumentId)
      search.set("excludeDocumentId", params.excludeDocumentId);
    const res = await fetch(
      `${baseUrl}/next-document-number?${search.toString()}`,
      { credentials: "include" },
    );
    if (!res.ok)
      throw new Error(
        `Failed to fetch next document number: ${res.statusText}`,
      );
    return res.json();
  },

  async createDocument(input: CreateDocumentDTO): Promise<{ id: string }> {
    const res = await fetch(baseUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (!res.ok)
      throw new Error(`Failed to create document: ${res.statusText}`);
    return res.json();
  },

  async updateDocument(
    id: string,
    input: CreateDocumentDTO,
  ): Promise<{ id: string }> {
    const res = await fetch(`${baseUrl}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (!res.ok) await throwQuotationInvoiceHttpError(res, "Failed to update document");
    return res.json();
  },

  async updateDocumentStatus(
    id: string,
    status: "issued" | "cancelled",
  ): Promise<{ ok: boolean }> {
    const res = await fetch(`${baseUrl}/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    if (!res.ok)
      await throwQuotationInvoiceHttpError(
        res,
        "Failed to update document status",
      );
    return res.json();
  },

  async deleteDocument(id: string): Promise<{ ok: boolean }> {
    const res = await fetch(`${baseUrl}/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to delete document: ${res.statusText}`);
    return res.json();
  },

  async convertToInvoice(
    id: string,
    payload: ConvertQuotationToInvoiceBody,
  ): Promise<{ id: string }> {
    const res = await fetch(`${baseUrl}/${id}/convert-to-invoice`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    const resBody = (await res.json().catch(() => ({}))) as {
      id?: string;
      message?: string;
      error?: string;
    };
    if (!res.ok) {
      throw new Error(
        resBody.message ??
          resBody.error ??
          `convert-to-invoice: ${res.statusText}`,
      );
    }
    if (!resBody.id) throw new Error("Invalid response from convert-to-invoice");
    return { id: resBody.id };
  },

  async convertToSalesOrder(id: string): Promise<{
    salesOrderId: string;
    orderNo: string;
  }> {
    const res = await fetch(`${baseUrl}/${id}/convert-to-sales-order`, {
      method: "POST",
      credentials: "include",
    });
    const body = (await res.json().catch(() => ({}))) as {
      salesOrderId?: string;
      orderNo?: string;
      message?: string;
      error?: string;
      invalidLines?: Array<{
        orderIndex: number;
        description: string;
        reason: string;
      }>;
    };
    if (!res.ok) {
      const err = new Error(
        body.message ?? body.error ?? `convert-to-sales-order: ${res.statusText}`,
      ) as Error & {
        code?: string;
        invalidLines?: typeof body.invalidLines;
      };
      err.code = body.error;
      err.invalidLines = body.invalidLines;
      throw err;
    }
    if (!body.salesOrderId || !body.orderNo) {
      throw new Error("Invalid response from convert-to-sales-order");
    }
    return { salesOrderId: body.salesOrderId, orderNo: body.orderNo };
  },

  async convertToReceipt(id: string, paymentId: string): Promise<{ id: string }> {
    const res = await fetch(`${baseUrl}/${id}/convert-to-receipt`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ paymentId }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      id?: string;
      message?: string;
      error?: string;
    };
    if (!res.ok) {
      throw new Error(
        body.message ?? body.error ?? `convert-to-receipt: ${res.statusText}`,
      );
    }
    if (!body.id) throw new Error("Invalid response from convert-to-receipt");
    return { id: body.id };
  },

  async list(
    query: Partial<OffsetPageQueryDTO> = {},
  ): Promise<DocumentsListResult> {
    const params = new URLSearchParams();
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok)
      throw new Error(`Failed to fetch documents: ${res.statusText}`);
    return res.json();
  },

  async getSummary(): Promise<DocumentsSummaryResult> {
    const res = await fetch(`${baseUrl}/summary`, { credentials: "include" });
    if (!res.ok) throw new Error(`Failed to fetch summary: ${res.statusText}`);
    return res.json();
  },

  async getIssuerSettings(): Promise<QuotationInvoiceIssuerSettingsDTO> {
    const res = await fetch(`${baseUrl}/issuer-settings`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch issuer settings: ${res.statusText}`);
    return res.json();
  },

  async updateIssuerSettings(
    input: UpdateIssuerSettingsInput,
  ): Promise<QuotationInvoiceIssuerSettingsDTO> {
    const res = await fetch(`${baseUrl}/issuer-settings`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (!res.ok)
      throw new Error(`Failed to update issuer settings: ${res.statusText}`);
    return res.json();
  },

  async get(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, { credentials: "include" });
    if (!res.ok) throw new Error(`Failed to fetch document: ${res.statusText}`);
    return res.json();
  },

  async getDetail(id: string): Promise<DocumentDetailResult> {
    const res = await fetch(`${baseUrl}/${id}/detail`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch document detail: ${res.statusText}`);
    const data = (await res.json()) as DocumentDetailResult;
    return {
      ...data,
      payments: data.payments ?? [],
      linkedInvoices: data.linkedInvoices ?? [],
      linkedSalesOrder: data.linkedSalesOrder ?? null,
      quotationInvoiceHeadroom: data.quotationInvoiceHeadroom ?? null,
    };
  },

  async createDocumentPayment(
    documentId: string,
    input: CreateDocumentPaymentInput,
  ): Promise<{ id: string }> {
    const res = await fetch(`${baseUrl}/${documentId}/payments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (res.status === 400) {
      const body = (await res.json()) as { error?: string };
      if (body.error === "EXCEEDS_TOTAL") throw new Error("ຍອດຮັບເກີນຍອດເອກະສານ");
      if (body.error === "BAD_STATUS")
        throw new Error("ບໍ່ສາມາດບັນທຶກການຊຳລະສຳລັບສະຖານະນີ້");
      throw new Error("ບໍ່ສາມາດບັນທຶກ");
    }
    if (!res.ok) throw new Error(`Failed to create payment: ${res.statusText}`);
    return res.json();
  },

  async updateDocumentPayment(
    paymentId: string,
    input: UpdateDocumentPaymentInput,
  ): Promise<{ ok: boolean }> {
    const res = await fetch(`${baseUrl}/payments/${paymentId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
    if (res.status === 400) {
      const body = (await res.json()) as { error?: string };
      if (body.error === "EXCEEDS_TOTAL") throw new Error("ຍອດຮັບເກີນຍອດເອກະສານ");
      throw new Error("ບໍ່ສາມາດບັນທຶກ");
    }
    if (!res.ok) throw new Error(`Failed to update payment: ${res.statusText}`);
    return res.json();
  },

  async deleteDocumentPayment(paymentId: string): Promise<{ ok: boolean }> {
    const res = await fetch(`${baseUrl}/payments/${paymentId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to delete payment: ${res.statusText}`);
    return res.json();
  },

  async getHistory(
    params: {
      offset?: number;
      limit?: number;
      dateFrom?: string;
      dateTo?: string;
      documentType?: string;
      action?: string;
      q?: string;
    } = {},
  ): Promise<HistoryListResult> {
    const search = new URLSearchParams();
    if (params.offset != null) search.set("offset", String(params.offset));
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.dateFrom != null) search.set("dateFrom", params.dateFrom);
    if (params.dateTo != null) search.set("dateTo", params.dateTo);
    if (params.documentType != null)
      search.set("documentType", params.documentType);
    if (params.action != null) search.set("action", params.action);
    if (params.q != null) search.set("q", params.q);
    const res = await fetch(`${baseUrl}/history?${search.toString()}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch history: ${res.statusText}`);
    return res.json();
  },

  async getHistorySummary(): Promise<HistorySummaryResult> {
    const res = await fetch(`${baseUrl}/history/summary`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch history summary: ${res.statusText}`);
    return res.json();
  },

  async listCatalogProducts(params: {
    q?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
    sellableOnly?: boolean;
  }) {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.categoryId) search.set("categoryId", params.categoryId);
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.offset != null) search.set("offset", String(params.offset));
    if (params.sellableOnly === false) search.set("sellableOnly", "false");
    const res = await fetch(`${baseUrl}/catalog/products?${search}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to list catalog products: ${res.statusText}`);
    return res.json() as Promise<{
      data: import("./catalog-types").QiCatalogProductDTO[];
      meta: { total: number; limit: number; offset: number };
    }>;
  },

  async listCatalogRawMaterials(params: {
    q?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }) {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.categoryId) search.set("categoryId", params.categoryId);
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.offset != null) search.set("offset", String(params.offset));
    const res = await fetch(`${baseUrl}/catalog/raw-materials?${search}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to list raw materials: ${res.statusText}`);
    return res.json() as Promise<{
      data: import("./catalog-types").QiCatalogRawMaterialDTO[];
      meta: { total: number; limit: number; offset: number };
    }>;
  },

  async listCatalogServices(params: {
    q?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }) {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.categoryId) search.set("categoryId", params.categoryId);
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.offset != null) search.set("offset", String(params.offset));
    const res = await fetch(`${baseUrl}/catalog/services?${search}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to list services: ${res.statusText}`);
    return res.json() as Promise<{
      data: import("./catalog-types").QiCatalogServiceDTO[];
      meta: { total: number; limit: number; offset: number };
    }>;
  },

  async listCatalogCategories() {
    const res = await fetch(`${baseUrl}/catalog/categories`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to list categories: ${res.statusText}`);
    return res.json() as Promise<{
      items: import("./catalog-types").QiCatalogCategoryDTO[];
    }>;
  },

  async listHeadOfficeBranches() {
    const res = await fetch(`${baseUrl}/catalog/head-office-branches`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to list HQ branches: ${res.statusText}`);
    return res.json() as Promise<{
      items: import("./catalog-types").QiHeadOfficeBranchDTO[];
    }>;
  },

  async getProductAvailability(params: {
    productId: string;
    branchId: string;
    excludeQuotationId?: string;
  }) {
    const search = new URLSearchParams({ branchId: params.branchId });
    if (params.excludeQuotationId) {
      search.set("excludeQuotationId", params.excludeQuotationId);
    }
    const res = await fetch(
      `${baseUrl}/catalog/products/${params.productId}/availability?${search}`,
      { credentials: "include" },
    );
    if (!res.ok) {
      await throwQuotationInvoiceHttpError(res, "Failed to load availability");
    }
    return res.json() as Promise<import("./catalog-types").QiProductAvailabilityDTO>;
  },

  async checkIssueStock(documentId: string) {
    const res = await fetch(`${baseUrl}/${documentId}/issue-stock-check`, {
      credentials: "include",
    });
    if (!res.ok) {
      await throwQuotationInvoiceHttpError(res, "Failed to check stock");
    }
    return res.json() as Promise<{
      ok: boolean;
      shortfalls: import("./catalog-types").QiStockShortfallDTO[];
    }>;
  },
};
